// Отбор событий Hawk, не зависящий от DOM.
//
// Отдельный файл, а не часть `hawk-browser.ts`, по той же причине, по которой
// рядом живут `hawk-scrub` и `hawk-throttle`: браузерный кэтчер ИСКЛЮЧЁН из
// tsconfig пакета (ему нужен DOM lib, которого здесь нет) и компилируется
// приложениями. Тест, импортирующий его, затащил бы файл обратно в программу и
// уронил `pnpm typecheck` четырнадцатью ошибками про `window`. Чистая логика
// отбора живёт здесь и проверяется в проекте `unit`.

/**
 * Заголовки, под которыми SDK шлёт ИЗМЕРЕНИЯ, а не дефекты (`Long Task`,
 * `Long Animation Frame`, `Poor Web Vital: …`). Их отбор отличается от отбора
 * ошибок: у ошибки ценность не зависит от того, кто её словил, у измерения —
 * зависит целиком.
 */
const PERFORMANCE_ISSUE_PREFIXES = ["long task", "long animation frame", "poor web vital"] as const;

/**
 * Автоматика вместо человека — по двум признакам:
 *
 * - `navigator.webdriver` ставит любой браузер под управлением WebDriver/CDP
 *   (headless Chrome, Puppeteer, Playwright, синтетические мониторы);
 * - имя в UA — у тех краулеров, которые представляются честно.
 *
 * Полным этот список не будет никогда: UA подделывается одной строкой, и
 * событие с «Chrome/125 … Edge/12.246» (легаси-токен Edge на движке Chrome 125)
 * отличается от живого браузера только несочетаемостью версий. Фильтр закрывает
 * то, что закрывается надёжно, и не притворяется, что закрывает остальное.
 *
 * `bot` ищется только внутри известных имён и с границей слова: подстрока `bot`
 * живёт в UA настоящих телефонов (CUBOT), и наивный `includes("bot")` выкинул бы
 * живых пользователей.
 */
const SYNTHETIC_UA_PATTERN =
	/headless|phantomjs|puppeteer|playwright|lighthouse|pagespeed|gtmetrix|pingdom|uptimerobot|bytespider|facebookexternalhit|\b(?:crawler|spider|slurp)\b|(?:google|bing|yandex|ahrefs|semrush|petal|duck|apple|mj12|dot|uptime)bot\b/i;

/**
 * Ниже этого порога «длинный кадр» никто не почувствовал.
 *
 * Порог SDK стоит на `duration` кадра, а за отзывчивость отвечает
 * `blockingDuration` — сколько времени главный поток реально не отвечал на ввод.
 * Кадр на 729 мс с блокировкой 8 мс (событие с боя 22.08.2026) — это не
 * подвисание, а обычная отрисовка, разложенная браузером по кадру.
 */
const MIN_LOAF_BLOCKING_MS = 100;

/** Событие — метрика производительности, а не пойманное падение. */
export function isPerformanceIssueTitle(title: string): boolean {
	const normalized = title.toLowerCase();
	return PERFORMANCE_ISSUE_PREFIXES.some((prefix) => normalized.startsWith(prefix));
}

/**
 * Чистая половина проверки на автоматику: само чтение `navigator` живёт в
 * `hawk-browser.ts`, сюда приходят уже снятые значения.
 */
export function isSyntheticAgent(userAgent: string, webdriver: boolean): boolean {
	return webdriver || SYNTHETIC_UA_PATTERN.test(userAgent);
}

/** Чтение поля у значения неизвестной формы — без `as` и без падения. */
function fieldOf(value: unknown, key: string): unknown {
	if (typeof value !== "object" || value === null) return undefined;
	if (!(key in value)) return undefined;
	return Reflect.get(value, key);
}

/**
 * Длинный кадр, который никого не задержал: браузер потратил время на
 * отрисовку, а не на блокировку ввода. Форма payload'а — `addons["Long Frame"]`
 * SDK (`PERFORMANCE_ISSUE_ADDON_KEYS.longFrame`, менять нельзя: это провод).
 */
export function isLowValueLongFrame(title: string, addons: unknown): boolean {
	if (!title.toLowerCase().startsWith("long animation frame")) return false;
	const blocking = fieldOf(fieldOf(addons, "Long Frame"), "frameBlockingDurationMs");
	return typeof blocking === "number" && blocking < MIN_LOAF_BLOCKING_MS;
}
